// src/lib/layouts/applyEnforcedLayout.ts
import type { PostLayoutConfig } from './types';

export interface EnforcedPostLayout {
  kind: string;
  config: Record<string, unknown>;
  locked_fields?: string[];
}

/**
 * Config the editor should show under enforcement: enforced config, with the
 * persisted clip's values kept for UNLOCKED keys (when kinds match).
 *
 * Mirrors backend mergeEnforcedPostLayout semantics:
 * - Result kind is always enforced.kind.
 * - Start from enforced.config.
 * - For keys NOT in locked_fields, overlay the persisted value ONLY if
 *   persisted.kind === enforced.kind (a value for a different kind is meaningless).
 * - Locked keys always come from enforced.config.
 */
export function buildEnforcedInitialConfig(
  enforced: EnforcedPostLayout,
  persisted: { kind: string; [k: string]: unknown } | null,
): PostLayoutConfig {
  const lockedSet = new Set(enforcedLockedKeys(enforced));
  const base: Record<string, unknown> = { kind: enforced.kind, ...enforced.config };

  if (persisted && persisted.kind === enforced.kind) {
    // Overlay unlocked keys from persisted
    for (const key of Object.keys(persisted)) {
      if (key === 'kind') continue;
      if (!lockedSet.has(key)) {
        base[key] = persisted[key];
      }
    }
  }

  return base as unknown as PostLayoutConfig;
}

/**
 * Keys the settings panel must disable (render as locked).
 * Returns locked_fields filtered to strings, or [] when absent.
 */
export function enforcedLockedKeys(enforced: EnforcedPostLayout): string[] {
  if (!enforced.locked_fields) return [];
  return enforced.locked_fields.filter((f): f is string => typeof f === 'string');
}
